import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Volleyball, ArrowRight, CheckCircle2, Wrench } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

const LEAD_PROFILES = ['lecteur', 'parent', 'joueur', 'club', 'journaliste', 'partenaire', 'ambassadeur'];
const LEAD_INTERESTS = ['livre', 'masterclass', 'les_deux'];

export function videoEmbed(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) {
    return (
      <div className="relative flex-1 min-h-[280px]">
        <iframe src={`https://www.youtube.com/embed/${m[1]}`} title="Vidéo On croit qu'on joue !"
          data-testid="landing-video-iframe"
          className="absolute inset-0 w-full h-full rounded-2xl"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen />
      </div>
    );
  }
  return <video src={url} controls data-testid="landing-video-player" className="w-full flex-1 min-h-[280px] rounded-2xl object-cover" />;
}

export function LeadCapture({ t, maintenance = false }) {
  const [form, setForm] = useState({ nom: '', email: '', profile: '', interest: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.includes('@') || !form.profile || !form.interest) return;
    setSending(true);
    try {
      await api.post('/crm/leads', form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('landing.lead.error'));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="lead-success">
        <CheckCircle2 className="w-12 h-12 text-or-400 mx-auto mb-4" strokeWidth={2.5} />
        <p className="text-lg font-bold text-creme">{t('landing.lead.success')}</p>
      </div>
    );
  }
  const pill = (active) => `px-4 py-2 rounded-full border-2 text-sm font-bold transition-colors ${active ? 'bg-or-400 border-or-400 text-nuit-950' : 'border-nuit-600 text-brume hover:border-or-400/50 hover:text-creme'}`;
  return (
    <form onSubmit={submitLead} data-testid="lead-form">
      <h2 className="font-fredoka font-semibold text-2xl text-creme mb-2" data-testid="lead-title">
        {t(maintenance ? 'landing.lead.titleM' : 'landing.lead.title')}
      </h2>
      <p className="text-sm text-brume leading-relaxed mb-5">
        {t(maintenance ? 'landing.lead.descM' : 'landing.lead.desc')}
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Input data-testid="lead-name" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })}
          placeholder={t('landing.lead.name')}
          className="h-12 rounded-2xl text-base font-bold bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
        <Input type="email" data-testid="lead-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder={t('landing.lead.email')}
          className="h-12 rounded-2xl text-base font-bold bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
      </div>
      <p className="text-xs font-bold text-brume uppercase tracking-widest mb-2">{t('landing.lead.profileLabel')}</p>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="lead-profiles">
        {LEAD_PROFILES.map((p) => (
          <button type="button" key={p} onClick={() => setForm({ ...form, profile: p })} data-testid={`lead-profile-${p}`} className={pill(form.profile === p)}>
            {t(`landing.lead.profile.${p}`)}
          </button>
        ))}
      </div>
      <p className="text-xs font-bold text-brume uppercase tracking-widest mb-2">{t('landing.lead.interestLabel')}</p>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="lead-interests">
        {LEAD_INTERESTS.map((i) => (
          <button type="button" key={i} onClick={() => setForm({ ...form, interest: i })} data-testid={`lead-interest-${i}`} className={pill(form.interest === i)}>
            {t(`landing.lead.interest.${i}`)}
          </button>
        ))}
      </div>
      <Button type="submit" data-testid="lead-submit"
        disabled={sending || !form.nom.trim() || !form.email.includes('@') || !form.profile || !form.interest}
        className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform gap-2">
        {sending ? t('landing.checking') : t('landing.lead.submit')}
        <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
      </Button>
      <p className="text-xs text-brume mt-4">{t('landing.lead.rgpd')}</p>
    </form>
  );
}

export function MaintenanceScreen({ settings }) {
  const { t } = useI18n();
  const videoUrl = (settings?.landing_video_url || '').trim();
  return (
    <div className="min-h-screen bg-nuit-900" data-testid="maintenance-screen">
      <header className="border-b border-nuit-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 font-fredoka font-semibold text-lg text-or-400" data-testid="maintenance-brand">
            <span className="w-9 h-9 shrink-0 rounded-full bg-or-400 flex items-center justify-center">
              <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
            </span>
            <span className="truncate">On croit qu'on joue !</span>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <section className="relative">
        <img src="/images/maintenance_stade.jpg" alt={t('maintenance.title')}
          data-testid="maintenance-image"
          className="w-full h-[240px] sm:h-[360px] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-nuit-900 via-nuit-900/40 to-transparent" />
        <div className="absolute bottom-4 inset-x-0 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-or-400 text-nuit-950 text-sm font-bold px-5 py-2"
            data-testid="maintenance-badge">
            <Wrench className="w-4 h-4" strokeWidth={2.5} /> {t('maintenance.badge')}
          </span>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring}
          className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme font-fredoka"
            data-testid="maintenance-title">{t('maintenance.title')}</h1>
          <p className="text-base sm:text-lg text-brume leading-relaxed mt-4 max-w-2xl mx-auto"
            data-testid="maintenance-subtitle">{t('maintenance.subtitle')}</p>
        </motion.div>

        <div className={videoUrl ? 'grid lg:grid-cols-2 gap-8 items-stretch' : 'max-w-2xl mx-auto'}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.1 }}>
            <div className="bg-nuit-800/90 backdrop-blur-xl border border-or-400/30 rounded-3xl p-6 sm:p-8 shadow-2xl gold-glow h-full"
              data-testid="maintenance-lead-card">
              <LeadCapture t={t} maintenance />
            </div>
          </motion.div>
          {videoUrl && (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.15 }}
              data-testid="maintenance-video">
              <div className="bg-nuit-800/90 backdrop-blur-xl border border-or-400/30 rounded-3xl p-4 sm:p-5 shadow-2xl gold-glow h-full flex flex-col">
                <h2 className="font-fredoka font-semibold text-xl text-creme mb-3 px-2">{t('landing.videoTitle')}</h2>
                {videoEmbed(videoUrl)}
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
